import React, {useEffect, useRef, useState} from 'react';
import {toast} from 'react-toastify';
import {z} from 'zod';
import {useForm} from 'react-hook-form';
import {zodResolver} from '@hookform/resolvers/zod';
import {CameraIcon} from '@heroicons/react/24/outline';
import type {WebGLRenderer} from 'three';
import {getGetMeshesQueryKey, useUpdateMesh, type MeshType, type UpdateMeshBody} from 'api/generated';
import {Button, InputText, Modal, ModelViewer} from 'components';
import {cacheUpdateItem} from 'utils';
import {imgNone} from 'assets';

const schema = z.object({
  name: z.string().min(1, 'Name is required'),
  image: z.instanceof(Blob).optional(),
});

interface IModalModelUpdateProps {
  visible: boolean;
  onClose: () => void;
  mesh: MeshType | undefined;
}

const ModalModelUpdate = ({visible, onClose, mesh}: IModalModelUpdateProps) => {
  const glRef = useRef<WebGLRenderer>(null);
  return (
    <Modal visible={visible} onClose={onClose} size="large" scrollable={false}>
      {!!mesh && (
        <div className="flex flex-col sm:flex-row gap-5 h-[calc(100svh-100px)] sm:h-[calc(100svh-300px)]">
          <ModelViewer key={mesh.uid} glRef={glRef} mesh={mesh} className="w-full aspect-square sm:w-5/8 rounded-md overflow-hidden" />
          <div className="w-full shrink-0 sm:w-3/8 overflow-y-scroll no-scrollbar">
            <Form glRef={glRef} mesh={mesh} visible={visible} onClose={onClose} />
          </div>
        </div>
      )}
    </Modal>
  );
};

export default React.memo(ModalModelUpdate);

interface IFormProps {
  glRef: ReturnType<typeof useRef<WebGLRenderer | null>>;
  mesh: MeshType;
  visible: boolean;
  onClose: () => void;
}

const Form = ({glRef, mesh, visible, onClose}: IFormProps) => {
  const [screenshot, setScreenshot] = useState('');

  const updateMesh = useUpdateMesh();
  const {
    register,
    reset,
    setValue,
    handleSubmit,
    formState: {errors},
  } = useForm({
    resolver: zodResolver(schema),
  });

  const handleScreenshot = () => {
    glRef.current?.domElement?.toBlob(
      blob => {
        if (blob) {
          setValue('image', blob);
          const reader = new FileReader();
          reader.onloadend = () => {
            if (reader.result) {
              setScreenshot(reader.result as string);
            }
          };
          reader.readAsDataURL(blob);
          toast.info('Screenshot taken');
        }
      },
      'image/jpeg',
      0.7,
    );
  };

  const onSubmit = async (data: UpdateMeshBody) => {
    const {data: res} = await updateMesh.mutateAsync({uid: mesh!.uid, data});
    if (res.data) {
      onClose();
      toast.success('Model updated');
      cacheUpdateItem({
        queryKey: [getGetMeshesQueryKey({filter: 'mine'})[0]],
        data: res.data,
      });
    } else if (res.errors) {
      toast.error(res.errors[0].detail);
    }
  };

  useEffect(() => {
    if (visible && mesh) {
      reset(mesh);
    }
    if (!visible) {
      setScreenshot('');
    }
  }, [visible, mesh]);

  return (
    <form onSubmit={handleSubmit(onSubmit)} className="flex flex-col h-full justify-between">
      <div className="space-y-2">
        <div className="flex flex-col gap-2">
          <label className="font-medium text-sm/6 text-gray-900 dark:text-white">Image</label>
          <div className="relative group w-1/2 self-center active:scale-90 duration-100 rounded-md overflow-hidden">
            <img src={screenshot || (mesh.imageUrl ?? imgNone)} alt={mesh.name} className="aspect-square object-cover" onClick={handleScreenshot} />
            <button type="button" className="absolute inset-0 flex justify-center items-center cursor-pointer" onClick={handleScreenshot}>
              <div className="absolute inset-0 duration-300 bg-black opacity-0 group-hover:opacity-50"></div>
              <div className="relative text-white opacity-0 group-hover:opacity-100 duration-200 flex items-center flex-col">
                <CameraIcon className="size-20" />
                <p className="font-medium">Click to take a screenshot</p>
              </div>
            </button>
          </div>
        </div>
        <InputText label="Name" {...register('name')} error={errors.name?.message} />
      </div>
      <div className="mt-5 w-full flex items-center justify-between">
        <div className="w-20">
          <Button text="Cancel" fullWidth size="extra-small" variant="secondary" disabled={updateMesh.isPending} onClick={onClose} />
        </div>
        <div className="w-20">
          <Button type="submit" fullWidth text="Save" size="extra-small" loading={updateMesh.isPending} />
        </div>
      </div>
    </form>
  );
};
